import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { Search, Moon, Sun } from "lucide-react";
import AIChatbot from "@/components/AIChatbot";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "next-themes";
import NotificationCenter from "@/components/NotificationCenter";
import { CommandPalette } from "@/components/CommandPalette";
import ProactiveInsightsBanner from "@/components/ProactiveInsightsBanner";
import ConnectivityBanner from "@/components/ConnectivityBanner";
import BottomNav from "@/components/BottomNav";
import GlobalErrorBoundary from "@/components/GlobalErrorBoundary";
import SetupWizard from "@/components/setup-wizard/SetupWizard";
import ShortcutsOverlay from "@/components/ShortcutsOverlay";
import { useOnboardingGate } from "@/hooks/useOnboardingGate";
import { useSidebarPersistence } from "@/hooks/useSidebarPersistence";
import SidebarMobileAutoClose from "@/components/SidebarMobileAutoClose";

export default function AppLayout() {
  const [cmdOpen, setCmdOpen] = useState(false);
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const { shouldOpen: setupOpen, dismiss: dismissSetup } = useOnboardingGate();
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebarPersistence();
  const navigate = useNavigate();
  const initials = user?.user_metadata?.full_name
    ? user.user_metadata.full_name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() || "U";

  // Apply persisted density on mount
  useEffect(() => {
    try {
      const d = localStorage.getItem("lovable.density") || "comfortable";
      document.documentElement.setAttribute("data-density", d);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
      {/* Skip to content for keyboard users */}
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <SidebarMobileAutoClose />
      <div className="ambient-bloom min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <ConnectivityBanner />
          <header className="h-14 flex items-center justify-between border-b border-border/50 bg-card/80 backdrop-blur-xl px-4 shrink-0 sticky top-0 z-40">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="hover:bg-muted/80 transition-colors" aria-label="Toggle sidebar" />
              <button
                onClick={() => setCmdOpen(true)}
                aria-label="Open command palette"
                className="relative hidden md:flex items-center h-9 w-72 pl-9 pr-12 text-sm bg-muted/40 border border-border/50 rounded-lg hover:bg-muted/60 hover:border-primary/30 transition-all cursor-pointer text-muted-foreground"
              >
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
                <span>Search reports, sites, alerts…</span>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 text-muted-foreground/50">
                  <kbd className="text-[10px] border border-border/50 rounded px-1 py-0.5 bg-muted/50">⌘</kbd>
                  <kbd className="text-[10px] border border-border/50 rounded px-1 py-0.5 bg-muted/50">K</kbd>
                </div>
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 md:hidden hover:bg-muted/80 transition-colors"
                onClick={() => setCmdOpen(true)}
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-muted/80 transition-colors"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                <span className="sr-only">Toggle theme</span>
              </Button>
              <NotificationCenter />
              <button
                onClick={() => navigate("/profile")}
                aria-label="Open profile"
                className="ml-1 rounded-full"
              >
                <Avatar className="h-8 w-8 ring-2 ring-primary/20 ring-offset-2 ring-offset-background transition-all hover:ring-primary/40">
                  <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-primary-foreground text-xs font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </div>
          </header>

          {/* Proactive Insights Banner */}
          <div className="px-4 pt-3">
            <ProactiveInsightsBanner />
          </div>

          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 overflow-auto pb-16 md:pb-0 focus:outline-none"
          >
            <GlobalErrorBoundary>
              <Outlet />
            </GlobalErrorBoundary>
          </main>

          <AIChatbot />
          <BottomNav />
        </div>
      </div>
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
      <ShortcutsOverlay />
      <SetupWizard open={setupOpen} onOpenChange={(o) => !o && dismissSetup()} />
    </SidebarProvider>
  );
}
